import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import sortBy from "lodash/sortBy";
import { GetServerSidePropsContext } from "next";

import { getBrand } from "api/requests/getBrand";
import { getBrands } from "api/requests/getBrands";
import { getBrandV3Show } from "api/requests/getBrandV3Show";
import { getIsFollowing } from "api/requests/getIsFollowing";
import getSearchResults from "api/requests/getSearchResults";
import getSponsoredArticles, {
  SponsoredArticle,
} from "api/requests/getSponsoredArticles";
import { getUserLocationData } from "api/requests/getUserLocation";
import searchProducts from "api/requests/searchProducts";
import { Brand, ProductCategory, ProductsByCategory } from "custom-types/Brand";
import { MenuItem } from "custom-types/MenuItem";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";

import NewBrandExperience from "./new";
import OldBrandExperience from "./old";

const BrandPage = ({
  brand,
  isFollowing,
  nearbyBrands,
  nearbyProducts,
  newBrands,
  brandsNewExperienceTreatment,
  sponsoredArticles,
  initialCategory,
  productsForCategoryCarousels,
  sortedCategoriesByCount,
  allBrands,
}: {
  brand: Brand;
  isFollowing: boolean;
  nearbyBrands: Brand[];
  nearbyProducts: MenuItem[];
  newBrands: Brand[];
  sponsoredArticles: SponsoredArticle[];
  brandsNewExperienceTreatment: boolean;
  productsForCategoryCarousels: ProductsByCategory;
  sortedCategoriesByCount: ProductCategory[];
  initialCategory: string;
  allBrands: Brand[];
}) => {
  return (
    <>
      {brandsNewExperienceTreatment ? (
        <NewBrandExperience
          brand={brand}
          isFollowing={isFollowing}
          nearbyBrands={nearbyBrands}
          nearbyProducts={nearbyProducts}
          newBrands={newBrands}
          productsForCategoryCarousels={productsForCategoryCarousels}
          sortedCategories={sortedCategoriesByCount}
          sponsoredArticles={sponsoredArticles}
          allBrands={allBrands}
        />
      ) : (
        <OldBrandExperience brand={brand} initialCategory={initialCategory} />
      )}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { query } = context;
  const { slug } = query;
  const overrideFlag = query?.new?.toString().toLowerCase() === "true";

  const {
    brandsNewExperience_flag: { treatment: brandsNewExperience },
  } = await getServerSideSplitTreatments(
    "brandsNewExperience_flag",
    getSplitKey(context)
  );

  const brandsNewExperienceTreatment =
    brandsNewExperience === "on" || overrideFlag;

  if (brandsNewExperienceTreatment) {
    const brand = await getBrandV3Show(String(slug));

    if (!brand) {
      return {
        notFound: true,
      };
    }

    const countryCode = getCountryCodeHeader(context);

    const { coordinates } = await getUserLocationData(countryCode, context);

    const getNearbyProducts = async () => {
      if (coordinates && brand.brandFeatureTier === "paid") {
        const { menuItem } = await getSearchResults(
          {
            "filter[brand_slug]": brand.slug,
            "filter[max_distance]": "20",
            "filter[type]": "menu_item",
          },
          { lat: coordinates.latitude, lon: coordinates.longitude },
          20,
          countryCode,
          12,
          0,
          undefined,
          true
        );
        return menuItem;
      }

      return [];
    };

    const sortedCategoriesByCount = sortBy(
      brand.productCategories,
      "productCount"
    ).reverse();

    const getProductsByCategory = async () => {
      const productsByCategory: ProductsByCategory = {};
      if (!brand.productCategories) {
        return productsByCategory;
      }

      const productsByCategoryArrays = await Promise.all(
        sortedCategoriesByCount.map(async (category) => {
          const { products } = await searchProducts(
            {
              filter: { brands: [brand.name], category: category.slug },
              take: 8,
            },
            countryCode,
            { lat: coordinates?.latitude, lon: coordinates?.longitude }
          );
          return products || [];
        })
      );

      sortedCategoriesByCount.forEach((category, i) => {
        productsByCategory[category.slug] = productsByCategoryArrays[i];
      });

      return productsByCategory;
    };

    const [
      nearbyProducts,
      isFollowing,
      productsForCategoryCarousels,
      SponsoredArticles,
    ] = await Promise.all([
      getNearbyProducts(),
      getIsFollowing({
        countryCode,
        followeeId: brand?.id,
        followeeType: "brand",
      }),
      getProductsByCategory(),
      getSponsoredArticles({
        id: brand.id,
        sponsorType: "brand",
      }),
    ]);

    let nearbyBrands: Brand[] = [];
    let newBrands: Brand[] = [];
    let allBrands: Brand[] = [];
    if (coordinates && brand.brandFeatureTier === "info") {
      const params = {
        coordinates: {
          lat: coordinates.latitude,
          lon: coordinates.longitude,
        },
        countryCode,
        take: 10,
      };

      const [{ data: nearbyResults }, { data: newResults }] = await Promise.all(
        [
          getBrands({
            ...params,
            filter: {
              "filter[brand_feature_tier]": "paid",
              "filter[max_distance]": "50mi",
            },
          }),
          getBrands({
            ...params,
            filter: {
              "filter[brand_feature_tier]": "paid",
              "filter[max_distance]": "50mi",
            },
            sort: {
              "sort[][created]": "asc",
            },
          }),
        ]
      );

      nearbyBrands = nearbyResults;
      newBrands = newResults;

      if (nearbyBrands.length === 0 && newBrands.length === 0) {
        const params = {
          countryCode,
          take: 10,
        };

        const [{ data: results }] = await Promise.all([
          getBrands({
            ...params,
            filter: {
              "filter[brand_feature_tier]": "paid",
            },
            sort: {
              "sort[][name]": "asc",
            },
          }),
        ]);

        allBrands = results;
      }
    }

    return {
      props: {
        SponsoredArticles,
        allBrands,
        brand,
        brandsNewExperienceTreatment,
        isFollowing,
        nearbyBrands,
        nearbyProducts,
        newBrands,
        productsForCategoryCarousels,
        sortedCategoriesByCount,
      },
    };
  } else {
    const initialCategory = query?.productCategory
      ? String(query.productCategory).toLowerCase()
      : "";
    const brand = await getBrand(String(slug));

    if (!brand) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        brand,
        initialCategory,
      },
    };
  }
};

export default BrandPage;
